import { InferenceSession } from 'onnxruntime-web';

export class ScanResult {
  text: string;
  confidence: number;
  location?: number[][];

  constructor(scanResult: Partial<ScanResult>) {
    this.text = scanResult.text;
    this.confidence = scanResult.confidence;
    this.location = scanResult.location;
  }
}

export class ImageRaw {
  data: Uint8Array | Uint8ClampedArray;
  #imageData: ImageData;
  #canvas: HTMLCanvasElement;
  width: ImageRawData['width'];
  height: ImageRawData['height'];

  getImageRawData(): ImageRawData {
    return { data: this.data, width: this.width, height: this.height }
  }

  static async open(url: string): Promise<ImageRaw> {
    const image = await imageFromUrl(url)
    const canvas = document.createElement('canvas')
    canvasDrawImage(canvas, image, image.naturalWidth, image.naturalHeight)
    const imageData = canvasGetImageData(canvas)
    return new ImageRaw({
      data: imageData.data,
      width: imageData.width,
      height: imageData.height,
    })
  }

  constructor({ data, width, height }: ImageRawData) {
    this.data = Uint8ClampedArray.from(data);
    this.width = width;
    this.height = height;
    const canvas = document.createElement('canvas');
    const imageData = new ImageData(this.data, width, height);
    canvasPutImageData(canvas, imageData);
    this.#canvas = canvas;
    this.#imageData = imageData;
  }

  // async write(path: string) {
  //   document.body.append(this.#canvas)
  // }

  async resize({ width, height }: SizeOption) {
    const newWidth = width || Math.round((this.width / this.height) * height!)
    const newHeight = height || Math.round((this.height / this.width) * width!)
    const newCanvas = document.createElement('canvas')
    canvasDrawImage(newCanvas, this.#canvas, newWidth, newHeight)
    const newImageData = canvasGetImageData(newCanvas)
    return this.#apply(newImageData)
  }

  async drawBox(lineImages: LineImage[]) {
    this.#ctx.strokeStyle = 'red'
    for (const lineImage of lineImages) {
      const [first, ...rests] = lineImage.box
      this.#ctx.beginPath()
      this.#ctx.moveTo(first[0], first[1])
      for (const rest of rests) {
        this.#ctx.lineTo(rest[0], rest[1])
      }
      this.#ctx.closePath()
      this.#ctx.stroke()
    }
    return this
  }

  get #ctx() {
    return this.#canvas.getContext('2d')!
  }

  #apply(imageData: ImageData) {
    canvasPutImageData(this.#canvas, imageData)
    this.#imageData = imageData
    this.data = imageData.data
    this.width = imageData.width
    this.height = imageData.height
    return this
  }

  #putImageData() {
    this.#canvas.width = this.width
    this.#canvas.height = this.height
    this.#ctx.putImageData(this.#imageData, 0, 0)
    return this
  }

  #drawImage(image: HTMLCanvasElement | HTMLImageElement | ImageBitmap, width?: number, height?: number) {
    canvasDrawImage(this.#canvas, image, width, height)
    return this
  }
}

function canvasDrawImage(canvas: HTMLCanvasElement, image:  HTMLCanvasElement | HTMLImageElement | ImageBitmap, width?: number, height?: number) {
  canvas.width = width || image.width
  canvas.height = height || image.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
}

function canvasPutImageData(canvas: HTMLCanvasElement, imageData: ImageData, width?: number, height?: number) {
  const ctx = canvas.getContext('2d')!
  canvas.width = width || imageData.width
  canvas.height = height || imageData.height
  ctx.putImageData(imageData, 0, 0)
}

function canvasGetImageData(canvas: HTMLCanvasElement) {
  return canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height)
}

async function imageFromUrl(url: string) {
  const image = new Image()
  image.src = url
  await image.decode()
  return image
}


export type ReshapeOptions = {
  mean?: number[];
  std?: number[];
};

export type ImageRawData = {
  data: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
};

export type ModelData = {
  data: number[] | Uint8Array;
  width: number;
  height: number;
};

export type Size = {
  width: number;
  height: number;
};

export type SizeOption = {
  width?: number;
  height?: number;
  fit?: 'contain' | 'cover' | 'fill' | 'inside' | 'outside';
};

export type LineImage = {
  image: ImageRaw;
  box: number[][];
};

export type Region = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type Line = {
  text: string;
  mean: number;
  box?: number[][];
};

export type Dictionary = string[];

export type Point = [x: number, y: number];

// export interface ModelBaseConstructorArg {
//   model: InferenceSession
//   options: ModelBaseOptions
// };

// export interface ModelBaseOptions {
//   isDebug?: boolean
//   debugOutputDir?: string
// }

// export interface ModelCreateOptions extends ModelBaseOptions {
//   models: {
//     detectionModel: string;
//     recognitionModel: string;
//     dictionaryModel: string;
//   }
//   onnxOptions?: InferenceSession.SessionOptions
// }


export type BoxType = [Point, Point, Point, Point];